import { PaymentDueBanner } from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useFeatureFlag } from "../../../hooks/useFeatureFlags";
import { doctorsApi } from "../../../services/api/doctors.api";
import { RootState } from "../../../store/store";
import { DoctorLicence } from "../../../types/licence";
import { RoleEnum } from "../../../types/user.definition";

const DISMISS_KEY = "med360_licence_banner_dismissed";
const SHOW_WINDOW_DAYS = 7;

function getDaysUntil(dateStr: string): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  return Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Global payment reminder for independent doctors, mirroring the manager
 * dashboard's PaymentDueBanner: shown on every page when the licence payment
 * is due within 7 days or overdue. Facility-covered licences are excluded —
 * the facility is billed, not the doctor.
 */
export const LicencePaymentDueBanner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session } = useSelector((state: RootState) => state.session);
  const [licence, setLicence] = useState<DoctorLicence | null>(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "true"
  );

  const isDoctor = session?.user?.roleName === RoleEnum.DOCTOR;
  // No licence banner at all while the doctor licence feature is switched off.
  const isDoctorLicenceEnabled = useFeatureFlag("DOCTOR_LICENCE");

  useEffect(() => {
    if (!isDoctor || !isDoctorLicenceEnabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const result = await doctorsApi.getLicence();
        if (!cancelled) setLicence(result);
      } catch {
        // No licence or fetch error — banner simply stays hidden.
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isDoctor, isDoctorLicenceEnabled]);

  if (
    dismissed ||
    !isDoctor ||
    !isDoctorLicenceEnabled ||
    !licence ||
    licence.source === "FACILITY" ||
    licence.status !== "ACTIVE" ||
    !licence.paymentDueDate
  ) {
    return null;
  }

  const daysLeft = getDaysUntil(licence.paymentDueDate);

  if (daysLeft > SHOW_WINDOW_DAYS) return null;

  const isOverdue = daysLeft < 0;

  const title = isOverdue
    ? t("banner.payment.overdueTitle", { days: Math.abs(daysLeft) })
    : daysLeft === 0
      ? t("banner.payment.dueTodayTitle")
      : t("banner.payment.dueSoonTitle", { days: daysLeft });

  const subtitle = isOverdue
    ? t("banner.payment.overdueSubtitle")
    : t("banner.payment.dueSoonSubtitle");

  return (
    <PaymentDueBanner
      title={title}
      subtitle={subtitle}
      ctaLabel={t("banner.payment.cta")}
      onCtaClick={() => navigate("/doctor/licence")}
      onDismiss={() => {
        sessionStorage.setItem(DISMISS_KEY, "true");
        setDismissed(true);
      }}
      dismissLabel={t("common.close")}
    />
  );
};
